// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

// The player's preferred distance units, mirrored locally so every screen can render a
// distance synchronously.
//
// The profile on the server is the source of truth — it should follow the player to a new
// device — but distances are drawn on the hole screen long before a profile fetch could
// return. So the last known value is cached here and rehydrated at boot; the profile
// overwrites it whenever it loads or the setting is changed.
//
// This is presentation only. Everything stays stored and computed in metres, so a wrong or
// stale value here can never corrupt data — at worst a distance reads in the wrong unit
// until the profile loads.

import { create } from 'zustand';

import { DEFAULT_DISTANCE_UNIT, parseDistanceUnit, type DistanceUnit } from '@/features/units/distance';

const STORAGE_KEY = 'goffy:distanceUnit';

type AsyncStorageLike = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
};

let asyncStorage: AsyncStorageLike | null = null;
try {
  asyncStorage = require('@react-native-async-storage/async-storage').default as AsyncStorageLike;
} catch {
  asyncStorage = null;
}

const getLocalStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

let storageOverride: AsyncStorageLike | null = null;

const readStored = async (): Promise<string | null> => {
  if (storageOverride) return storageOverride.getItem(STORAGE_KEY);
  const local = getLocalStorage();
  if (local) return local.getItem(STORAGE_KEY);
  if (asyncStorage) return asyncStorage.getItem(STORAGE_KEY);
  return null;
};

const writeStored = async (value: string): Promise<void> => {
  if (storageOverride) return storageOverride.setItem(STORAGE_KEY, value);
  const local = getLocalStorage();
  if (local) {
    local.setItem(STORAGE_KEY, value);
    return;
  }
  if (asyncStorage) await asyncStorage.setItem(STORAGE_KEY, value);
};

type DistanceUnitState = {
  unit: DistanceUnit;
  /** True once storage has been read, or once a unit has been set explicitly. */
  hydrated: boolean;
  setUnit: (unit: DistanceUnit) => void;
};

export const useDistanceUnitState = create<DistanceUnitState>((set) => ({
  unit: DEFAULT_DISTANCE_UNIT,
  hydrated: false,
  setUnit: (unit) => {
    set({ unit, hydrated: true });
    void writeStored(unit).catch(() => {
      // Losing the mirror only costs a moment of metric on the next cold start.
    });
  },
}));

/**
 * Reads the cached unit. Runs on import so no screen has to remember to call it.
 *
 * A value already set explicitly wins over the read: the profile can land first.
 */
export const hydrateDistanceUnit = async (): Promise<void> => {
  try {
    const stored = await readStored();
    if (stored === null) {
      useDistanceUnitState.setState({ hydrated: true });
      return;
    }

    // Guard against a profile load or a user choice landing while the read was in flight.
    if (useDistanceUnitState.getState().hydrated) return;
    useDistanceUnitState.setState({ unit: parseDistanceUnit(stored), hydrated: true });
  } catch {
    useDistanceUnitState.setState({ hydrated: true });
  }
};

// A frame of metric before the cached value lands is harmless — nothing is gated on
// `hydrated`, and the numbers are correct either way, only their unit is momentarily wrong.
void hydrateDistanceUnit();

/** The hook screens use. Re-renders when the preference changes. */
export const useDistanceUnit = (): DistanceUnit => useDistanceUnitState((state) => state.unit);

export const __setDistanceUnitStorageForTests = (storage: AsyncStorageLike | null) => {
  storageOverride = storage;
};

export const __resetDistanceUnitForTests = () => {
  useDistanceUnitState.setState({ unit: DEFAULT_DISTANCE_UNIT, hydrated: false });
};
